// Pure drag-reorder computation for the task board, extracted from
// TaskBoard so the trickiest logic on the board is unit-testable.
//
// Two-step model, mirroring how dnd-kit previews a drag:
//
//   1. resolveDropTarget(optimisticTasks, activeId, overId, isBelowOver)
//      → where the card visually sits when released: a destination column
//        and an insertion index among the OTHER cards of that column. This
//        must agree with the preview the user watched (dnd-kit places a
//        downward drag BELOW the hovered card, an upward drag ABOVE it),
//        otherwise the card snaps to a different slot on release.
//
//   2. computeDragPatches(serverTasks, activeId, placement)
//      → the PATCH list realizing that placement against server state.
//        Normally ONE PATCH (integer midpoint between the new neighbors);
//        only when the integer gap between two neighbors is exhausted do
//        we fall back to renumbering the destination column at
//        POSITION_GAP strides.
//
// The split matters because TaskBoard moves the dragged task across
// columns optimistically DURING the drag (fractional positions, see
// handleDragOver). Step 1 reads that optimistic state — it is what the
// user sees. Step 2 reads the drag-start snapshot — it is what the server
// still believes — so a cross-column move always PATCHes the status even
// though the optimistic task already wears the new one.

export const POSITION_GAP = 100;
export const COLUMN_DROP_PREFIX = "column:";

// The one board ordering. Position ties are the NORM, not the exception —
// the model defaults position to 0 and nothing assigns one on create — so
// every comparison here breaks ties by id. TaskBoard renders columns with
// this same comparator; if the two ever disagree, the math computes slots
// for an order the user isn't looking at.
export const byBoardOrder = (a, b) => a.position - b.position || a.id - b.id;

const sortedColumn = (tasks, status, excludeId) =>
  tasks
    .filter((t) => t.status === status && t.id !== excludeId)
    .sort(byBoardOrder);

/**
 * Resolve where the dragged card visually sits at drop time.
 *
 * @param {Array} tasks - the OPTIMISTIC board tasks (mid-drag state)
 * @param {*} activeId - id of the dragged task
 * @param {*} overId - drop target: a task id, or "column:<STATUS>"
 * @param {boolean} isBelowOver - pointer below the hovered card's midline
 *   (used only for a cross-column drop straight onto a card)
 * @returns {{destStatus: string, insertIndex: number} | null}
 *   insertIndex indexes into the destination column WITHOUT the active task.
 */
export function resolveDropTarget(
  tasks,
  activeId,
  overId,
  isBelowOver = false,
) {
  const activeTask = tasks.find((t) => t.id === activeId);
  if (!activeTask || overId == null) return null;

  const currentSlot = (status) =>
    sortedColumn(tasks, status, activeId).filter(
      (t) => byBoardOrder(t, activeTask) < 0,
    ).length;

  if (typeof overId === "string" && overId.startsWith(COLUMN_DROP_PREFIX)) {
    const destStatus = overId.slice(COLUMN_DROP_PREFIX.length);
    // Already in this column (either it never left, or handleDragOver moved
    // it here): keep the slot the user is looking at. Forcing "end of
    // column" here would teleport the card away from its previewed spot.
    if (activeTask.status === destStatus) {
      return { destStatus, insertIndex: currentSlot(destStatus) };
    }
    // Fresh drop into a column's empty space: append.
    return {
      destStatus,
      insertIndex: sortedColumn(tasks, destStatus, activeId).length,
    };
  }

  // Dropped on itself — common after a cross-column hop, when the card's
  // own (optimistically moved) node is under the pointer. Keep its slot.
  if (overId === activeId) {
    return {
      destStatus: activeTask.status,
      insertIndex: currentSlot(activeTask.status),
    };
  }

  const overTask = tasks.find((t) => t.id === overId);
  if (!overTask) return null;
  const destStatus = overTask.status;
  const destBase = sortedColumn(tasks, destStatus, activeId);
  const overIndex = destBase.findIndex((t) => t.id === overId);
  if (overIndex === -1) return null;

  // Match dnd-kit's sortable preview: dragging down lands BELOW the
  // hovered card, dragging up lands ABOVE it. "Down" means the active
  // card RENDERS above the hovered one — board order, not raw positions,
  // which tie. Cross-column, "direction" is wherever the pointer sits
  // relative to the hovered card's midline.
  const after =
    activeTask.status === destStatus
      ? byBoardOrder(activeTask, overTask) < 0
      : isBelowOver;
  return { destStatus, insertIndex: after ? overIndex + 1 : overIndex };
}

/**
 * Compute the PATCH list realizing a placement against server state.
 *
 * @param {Array} tasks - tasks as the SERVER believes them (drag-start
 *   snapshot); only the active task may differ from the optimistic board.
 * @param {*} activeId - id of the dragged task
 * @param {{destStatus: string, insertIndex: number}} placement
 * @returns {Array<{id, payload: {position, status?}, nextPosition, nextStatus}>}
 *   Empty array when the drop is a no-op.
 */
export function computeDragPatches(tasks, activeId, placement) {
  if (!placement) return [];
  const { destStatus, insertIndex } = placement;
  const activeTask = tasks.find((t) => t.id === activeId);
  if (!activeTask) return [];

  const sameColumn = activeTask.status === destStatus;
  const destBase = sortedColumn(tasks, destStatus, activeId);
  const boundedIndex = Math.max(0, Math.min(insertIndex, destBase.length));

  const prev = destBase[boundedIndex - 1];
  const next = destBase[boundedIndex];

  // No-op: same column, same visual slot. Slot equality in board order —
  // NOT raw position comparisons, which misfire on tied positions (a
  // self-drop on an all-zeros column must not renumber anything).
  if (sameColumn) {
    const currentIndex = destBase.filter(
      (t) => byBoardOrder(t, activeTask) < 0,
    ).length;
    if (currentIndex === boundedIndex) return [];
  }

  // Midpoint placement — integer positions, so the midpoint must clear
  // both neighbors to count as "room".
  let newPosition = null;
  if (!prev && !next) {
    newPosition = 0;
  } else if (!prev) {
    newPosition = next.position - POSITION_GAP;
  } else if (!next) {
    newPosition = prev.position + POSITION_GAP;
  } else {
    const mid = Math.floor((prev.position + next.position) / 2);
    if (mid > prev.position && mid < next.position) {
      newPosition = mid;
    }
  }

  if (newPosition !== null) {
    const payload = { position: newPosition };
    if (!sameColumn) payload.status = destStatus;
    return [
      {
        id: activeId,
        payload,
        nextPosition: newPosition,
        nextStatus: destStatus,
      },
    ];
  }

  // Gap exhausted between the two neighbors: renumber the destination
  // column (only — removing the task from its source column leaves that
  // column's relative order intact).
  const newOrder = [...destBase];
  newOrder.splice(boundedIndex, 0, { ...activeTask, status: destStatus });

  const patches = [];
  newOrder.forEach((task, idx) => {
    const position = idx * POSITION_GAP;
    const isActive = task.id === activeId;
    const statusChanging = isActive && !sameColumn;
    if (task.position !== position || statusChanging) {
      const payload = { position };
      if (statusChanging) payload.status = destStatus;
      patches.push({
        id: task.id,
        payload,
        nextPosition: position,
        nextStatus: statusChanging ? destStatus : task.status,
      });
    }
  });
  return patches;
}
